import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { getBusinessHours, saveBusinessHours } from '../../services/api';
import Toggle from '../../components/Toggle';
import PageTour from '../../components/tenant/PageTour';

const TOUR_STEPS = [
  {
    selector: '[data-tour="hours-first-day"]',
    title: 'Dias de atendimento',
    content: 'Use o toggle de cada dia pra marcar quando você atende. Dias desligados nunca aparecem no link público.',
    placement: 'bottom',
  },
  {
    selector: '[data-tour="hours-first-times"]',
    title: 'Abertura e fechamento',
    content: 'Define a faixa em que você atende (ex: 08:00 às 18:00). Só gera slot dentro dessa janela.',
    placement: 'top',
  },
  {
    selector: '[data-tour="hours-first-slot"]',
    title: 'Intervalo (slot)',
    content: 'Slot de 30min = horários 08:00, 08:30, 09:00... É o tempo mínimo entre dois agendamentos no mesmo dia.',
    placement: 'top',
  },
  {
    selector: '[data-tour="hours-save"]',
    title: 'Salvar',
    content: 'Salve pra aplicar. Seu link público atualiza na hora — cliente nunca agenda em horário que você não atende.',
    placement: 'top',
  },
];

const DAY_NAMES = ['Domingo','Segunda-feira','Terça-feira','Quarta-feira','Quinta-feira','Sexta-feira','Sábado'];

const defaultHours = () => DAY_NAMES.map((name, i) => ({
  day_of_week: i, day_name: name,
  open_time: '08:00', close_time: '18:00',
  is_open: i !== 0 && i !== 6,
  slot_duration_minutes: 30,
}));

function toHHMM(t) {
  return t ? String(t).substring(0, 5) : '08:00';
}

const SLOT_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 20, label: '20 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hora' },
  { value: 90, label: '1h 30min' },
  { value: 120, label: '2 horas' },
];

export default function TenantBusinessHours() {
  const [hours,   setHours]   = useState(defaultHours());
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    getBusinessHours()
      .then(r => {
        const data = r.data.hours || [];
        if (!data.length) { setHours(defaultHours()); return; }
        const byDay = {};
        data.forEach(h => { byDay[h.day_of_week] = h; });
        setHours(defaultHours().map(def => {
          const h = byDay[def.day_of_week];
          if (!h) return def;
          return { ...def, open_time: toHHMM(h.open_time), close_time: toHHMM(h.close_time), is_open: !!h.is_open, slot_duration_minutes: h.slot_duration_minutes || 30 };
        }));
      })
      .catch(() => toast.error('Erro ao carregar horários'))
      .finally(() => setLoading(false));
  }, []);

  const toggleDay   = i => setHours(p => p.map((h, idx) => idx === i ? { ...h, is_open: !h.is_open } : h));
  const updateField = (i, field, val) => setHours(p => p.map((h, idx) => idx === i ? { ...h, [field]: val } : h));

  const onSave = async () => {
    for (const h of hours) {
      if (!h.is_open) continue;
      if (h.close_time <= h.open_time) {
        toast.error(`${h.day_name}: horário de fechamento deve ser após o de abertura.`);
        return;
      }
    }
    setSaving(true);
    try {
      await saveBusinessHours(hours.map(h => ({
        day_of_week: h.day_of_week,
        open_time: h.open_time + ':00',
        close_time: h.close_time + ':00',
        is_open: h.is_open ? 1 : 0,
        slot_duration_minutes: Number(h.slot_duration_minutes),
      })));
      toast.success('Horários salvos com sucesso');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar horários');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="page-body">
      <div className="page-header-row">
        <div className="page-header">
          <h2>Horários de Atendimento</h2>
          <p>Configure os dias e horários disponíveis para agendamento online.</p>
          <div style={{ marginTop: 8 }}>
            <PageTour tourId="business-hours" steps={TOUR_STEPS} />
          </div>
        </div>
        <button className={`btn btn-primary${saving ? ' btn-loading' : ''}`} data-tour="hours-save" onClick={onSave} disabled={saving}>
          {saving ? '' : 'Salvar horários'}
        </button>
      </div>

      <div className="info-box" style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <span>💡</span>
        <span>Slots de tempo são os intervalos mínimos entre agendamentos. Ex: 30 min = horários às 08:00, 08:30, 09:00…</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {hours.map((h, i) => (
          <div
            key={i}
            className="card"
            data-tour={i === 1 ? 'hours-first-day' : undefined}
            style={{
              padding: '14px 16px',
              opacity: h.is_open ? 1 : 0.7,
              transition: 'opacity 0.2s',
            }}
          >
            {/* Row 1: day name + toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: h.is_open ? 12 : 0 }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: h.is_open ? 'var(--gray-900)' : 'var(--gray-500)' }}>
                {h.day_name}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: h.is_open ? 'var(--success)' : 'var(--gray-400)' }}>
                  {h.is_open ? 'Aberto' : 'Fechado'}
                </span>
                <Toggle checked={h.is_open} onChange={() => toggleDay(i)} />
              </div>
            </div>

            {/* Row 2: time fields — only when open */}
            {h.is_open && (
              <div data-tour={i === 1 ? 'hours-first-times' : undefined} style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 10,
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Abertura
                  </label>
                  <input
                    type="time"
                    value={h.open_time}
                    onChange={e => updateField(i, 'open_time', e.target.value)}
                    style={{
                      width: '100%',
                      fontSize: 14,
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: '1px solid var(--gray-200)',
                      background: 'var(--surface)',
                      color: 'var(--gray-800)',
                      fontFamily: 'inherit',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Fechamento
                  </label>
                  <input
                    type="time"
                    value={h.close_time}
                    onChange={e => updateField(i, 'close_time', e.target.value)}
                    style={{
                      width: '100%',
                      fontSize: 14,
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: '1px solid var(--gray-200)',
                      background: 'var(--surface)',
                      color: 'var(--gray-800)',
                      fontFamily: 'inherit',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div data-tour={i === 1 ? 'hours-first-slot' : undefined}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Intervalo
                  </label>
                  <select
                    value={h.slot_duration_minutes}
                    onChange={e => updateField(i, 'slot_duration_minutes', Number(e.target.value))}
                    style={{
                      width: '100%',
                      fontSize: 14,
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: '1px solid var(--gray-200)',
                      background: 'var(--surface)',
                      color: 'var(--gray-800)',
                      fontFamily: 'inherit',
                      outline: 'none',
                      boxSizing: 'border-box',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 10px center',
                      paddingRight: 28,
                    }}
                  >
                    {SLOT_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Mobile save button at bottom */}
      <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
        <button className={`btn btn-primary${saving ? ' btn-loading' : ''}`} onClick={onSave} disabled={saving} style={{ minWidth: 160 }}>
          {saving ? '' : 'Salvar horários'}
        </button>
      </div>
    </div>
  );
}
